import $ from "jquery";
import { IncontactAuthContext } from "./components/IncontactAuthContext.js";
import { OpenIdDiscovery } from "./components/OpenIdDiscovery.js";
import { IncontactApiError } from "./components/IncontactApiError.js";
import { AgentStateEvent } from "./components/AgentStateEvent.js";
import { AgentSessionStartEvent } from "./components/AgentSessionStartEvent.js";
import { WorkItemEvent } from "./components/WorkItemEvent.js";
import { AgentSessionEndEvent } from "./components/AgentSessionEndEvent.js";
import { CallContactEvent } from "./components/CallContactEvent.js";
import { AgentErrorEvent } from "./components/AgentErrorEvent.js";
import { UpdateSkillsEvent } from "./components/UpdateSkillsEvent.js";
import { AgentLegEvent } from "./components/AgentLegEvent.js";
import { AgentInfo } from "./components/AgentInfo.js";
import { SkillInfo } from "./components/SkillInfo.js";
import { ScopedLogger } from "../utils/log.js";

const API_TIMEOUT = 20000;
const DEFAULT_ISSUER = "https://cxone.niceincontact.com";

export class IncontactAPI {
  static get DEFAULT_ISSUER() {
    return "https://cxone.niceincontact.com";
  }

  #currentAuth;
  #auth;
  #apiTimeout = 20000;
  #oid;
  #claims;
  #baseUri;
  #sessionId;
  #extraLogging;
  #sessionPrefix;
  agentId;

  /**
   * Expose the current sessionId (loaded from storage or set by reload/session calls).
   */
  get sessionId() {
    return this.#sessionId;
  }

  constructor(auth, prefix) {
    this.#auth = auth;
    this.#oid = new OpenIdDiscovery(auth.issuer, auth.userHub);
    this.#sessionPrefix = prefix;
    this.#currentAuth = new IncontactAuthContext();
    this.#extraLogging = window.location.href.toLowerCase().includes("localhost");
    // Scoped logger for API instance
    this.log = ScopedLogger(prefix);
    this.initializeSessionId();
  }

  initializeSessionId() {
    if (this.#currentAuth) {
      this.#baseUri = this.#currentAuth.apiEndpoint;
      this.#sessionId = sessionStorage.getItem(this.#getStorageKey("sessionId"));
    }
  }

  doDiscovery() {
    return this.#oid.doDiscovery();
  }

  #getStorageKey(key) {
    return `${this.#sessionPrefix}.${key}`;
  }

  #parseIdToken(idToken) {
    const payload = idToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload));
  }

  #parseTokenResponseFromJson(data) {
    if (!data.access_token) {
      this.log.warn(`[${this.#sessionPrefix}] Invalid token refresh response: %o`, data);
      return;
    }
    this.#currentAuth.accessToken = data.access_token;
    this.#currentAuth.refreshToken = data.refresh_token;
    this.#currentAuth.validUntil = Date.now() + (data.expires_in - 300) * 1000; // Refresh 5 minutes before expiration
    this.#currentAuth.tokenType = data.token_type;
    this.#currentAuth.idToken = data.id_token;
    this.#currentAuth.logoutUrl = this.#oid.logoutEndpoint;
    this.#claims = this.#parseIdToken(data.id_token);

    if (this.#claims.icAgentId) {
      sessionStorage.setItem(this.#getStorageKey("agentId"), this.#claims.icAgentId);
      this.#currentAuth.agentId = this.#claims.icAgentId;
      this.agentId = this.#claims.icAgentId;
      this.#currentAuth.apiExpiration = this.#claims.exp * 1000;
    }
  }

  #retrieveApiEndpoint(tenantId) {
    return new Promise((resolve, reject) => {
      $.ajax({
        cache: false,
        url: `https://cxone.niceincontact.com/.well-known/cxone-configuration?tenantId=${tenantId}`,
        timeout: this.#apiTimeout,
        method: "GET",
        accept: "application/json",
        success: resolve,
        error: (xhr, textStatus, errorThrown) => {
          this.log.warn(
            `[${this.#sessionPrefix}] Failed to retrieve API endpoints for tenant ${tenantId}: ${textStatus} ${errorThrown}`,
          );
          reject(xhr, textStatus, errorThrown);
        },
      });
    });
  }

  #parseCxoneConfig(cxone) {
    this.#currentAuth.apiEndpoint = cxone.api_endpoint + "/incontactapi/services";
    this.#baseUri = this.#currentAuth.apiEndpoint;
  }

  async authenticateWithCode(code) {
    await this.doDiscovery();
    this.log.info(`[${this.#sessionPrefix}] Authenticating with code %s %o`, code, this.#oid);
    const endpoint = this.#oid.tokenEndpoint;
    const header = "Basic " + btoa(`${this.#auth.clientId}:${this.#auth.clientSecret}`);
    const param = {
      client_id: this.#auth.clientId,
      grant_type: "authorization_code",
      code: code,
      redirect_uri: this.#auth.returnUrl,
    };

    this.log.info(`[${this.#sessionPrefix}] Endpoint: ${endpoint}, Params: %o`, param);
    return new Promise((resolve, reject) => {
      $.ajax({
        cache: false,
        url: endpoint,
        timeout: API_TIMEOUT,
        method: "POST",
        accept: "application/json",
        data: param,
        headers: { Authorization: header },
        success: async (data) => {
          this.#parseTokenResponseFromJson(data);
          try {
            const cxone = await this.#retrieveApiEndpoint(this.#claims.tenantId);
            this.#parseCxoneConfig(cxone);
            this.#currentAuth.save();
            resolve(cxone);
          } catch (err) {
            reject(err);
          }
        },
        error: (xhr, textStatus, errorThrown) => {
          this.log(
            `[${this.#sessionPrefix}] Failed to retrieve token for code ${code}: ${textStatus} ${errorThrown}`,
          );
          this.handleError(xhr, textStatus, errorThrown);
          reject(xhr, textStatus, errorThrown);
        },
      });
    });
  }

  async #doTokenRefresh() {
    const header = "Basic " + btoa(`${this.#auth.clientId}:${this.#auth.clientSecret}`);
    const params = new URLSearchParams({
      client_id: this.#auth.clientId,
      grant_type: "refresh_token",
      refresh_token: this.#currentAuth.refreshToken,
      scope: "openid",
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.#apiTimeout);

    try {
      const response = await fetch(this.#oid.tokenEndpoint, {
        method: "POST",
        headers: {
          Authorization: header,
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: params.toString(),
        signal: controller.signal,
        credentials: "omit",
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        if (this.#extraLogging) {
          this.log.warn(
            `[${this.#sessionPrefix}] Failed to refresh token: ${response.status} ${response.statusText} - ${errorText}`,
          );
        }
        throw new IncontactApiError(response.status, response.statusText, null, errorText);
      }

      const data = await response.json();
      this.#parseTokenResponseFromJson(data);
      this.#currentAuth.save();
      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === "AbortError") {
        if (this.#extraLogging) {
          this.log.error(`[${this.#sessionPrefix}] Token refresh request timeout`);
        }
        throw new IncontactApiError(408, "Request Timeout", error.message, null);
      }
      if (error instanceof IncontactApiError) {
        throw error;
      }
      if (this.#extraLogging) {
        this.log.error(`[${this.#sessionPrefix}] Token refresh error:`, error);
      }
      throw new IncontactApiError(0, "Network Error", error.message, null);
    }
  }

  set sessionId(sessionId) {
    this.#sessionId = sessionId;
    sessionStorage.setItem(this.#getStorageKey("sessionId"), sessionId);
  }

  async #sendRequest(method, relativeUri, payload, timeout = null) {
    if (this.#extraLogging) {
      console.debug(
        `[${this.#sessionPrefix}] sendRequest: %s %s%s %o`,
        method,
        this.#baseUri,
        relativeUri,
        payload,
      );
    }
    if (!this.#oid.tokenEndpoint) {
      await this.doDiscovery();
    }
    if (this.#currentAuth.validUntil < Date.now()) {
      await this.#doTokenRefresh();
    }

    const url = this.#baseUri + relativeUri;
    const headers = new Headers({
      Authorization: `${this.#currentAuth.tokenType} ${this.#currentAuth.accessToken}`,
      Accept: "application/json",
    });

    let body;
    if (payload) {
      if (typeof payload === "string") {
        // Assume URL encoded form data string
        headers.set("Content-Type", "application/x-www-form-urlencoded");
        body = payload;
      } else if (payload instanceof URLSearchParams) {
        headers.set("Content-Type", "application/x-www-form-urlencoded");
        body = payload.toString();
      } else if (typeof payload === "object") {
        // Send JSON
        headers.set("Content-Type", "application/json");
        body = JSON.stringify(payload);
      } else {
        // Fallback: send as is
        body = payload;
      }
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout ? timeout : this.#apiTimeout);

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: method === "GET" || method === "HEAD" ? undefined : body,
        signal: controller.signal,
        credentials: "omit",
      });

      clearTimeout(timeoutId);

      if (!response.ok && response.status !== 304) {
        const errorText = await response.text();
        let errDesc = null;
        try {
          errDesc = JSON.parse(errorText).error_description;
        } catch (_) {}
        if (response.status === 409 && errDesc === "SessionInProgress") {
          let sessionId = null;
          try {
            sessionId = JSON.parse(errorText).sessionId;
          } catch (_) {
            /* ignore */
          }
          if (this.#extraLogging) {
            this.log.info(
              `[${this.#sessionPrefix}] SessionInProgress; joining existing session ${sessionId}`,
            );
          }
          return { sessionId };
        }
        if (this.#extraLogging) {
          this.log.error(
            `[${this.#sessionPrefix}] Request failed: ${method} ${url}` +
              ` - Status: ${response.status} ${response.statusText}` +
              ` - Response: ${errorText}`,
          );
        }
        throw new IncontactApiError(response.status, response.statusText, errDesc, errorText);
      }

      if (response.status === 304) {
        if (this.#extraLogging) {
          this.log.info(`[${this.#sessionPrefix}] 304 No Events: ${method} ${url}`);
        }
        return {}; // or {} if you prefer
      }

      let data;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (this.#extraLogging) {
        this.log.info(
          `[${this.#sessionPrefix}] Response: %s %s%s %o => %o`,
          method,
          this.#baseUri,
          relativeUri,
          payload,
          data,
        );
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === "AbortError") {
        if (this.#extraLogging) {
          this.log.error(`[${this.#sessionPrefix}] Request timeout: ${method} ${url}`);
        }
        throw new IncontactApiError(408, "Request Timeout", error.message, null);
      }
      if (error instanceof IncontactApiError) {
        throw error;
      }
      if (this.#extraLogging) {
        this.log.error(
          `[${this.#sessionPrefix}] sendRequest error (from ${method} ${relativeUri}):`,
          error,
        );
      }
      throw new IncontactApiError(0, "Network Error", error.message, null);
    }
  }

  startSession(phoneNumber, stationId) {
    const relativeUri = "/v26.0/agent-sessions";
    const payload = {
      stationPhoneNumber: phoneNumber,
      stationId: stationId,
      inactivityTimeout: 60,
      inactivityForceLogout: true,
    };
    return this.#sendRequest("POST", relativeUri, payload).then((data) => {
      this.sessionId = data.sessionId;
      this.storeSessionData(phoneNumber, stationId, data.sessionId);
      return data;
    });
  }

  startSessionAsAgent(phoneNumber, stationId, agentId) {
    const relativeUri = "/v26.0/agent-sessions";
    const payload = {
      stationPhoneNumber: phoneNumber,
      stationId: stationId,
      inactivityTimeout: 60,
      inactivityForceLogout: true,
      asAgentId: agentId,
    };
    return this.#sendRequest("POST", relativeUri, payload).then((data) => {
      this.sessionId = data.sessionId;
      this.storeSessionData(phoneNumber, stationId, data.sessionId);
      return data;
    });
  }

  storeSessionData(phoneNumber, stationId, sessionId) {
    sessionStorage.setItem(this.#getStorageKey("sessionId"), sessionId);
    // TODO: determine why the following need to be stored still?
    sessionStorage.setItem(this.#getStorageKey("phoneNumber"), phoneNumber);
    sessionStorage.setItem(this.#getStorageKey("stationId"), stationId);
    sessionStorage.setItem(this.#getStorageKey("reloadSessionId"), sessionId);
  }

  joinSession() {
    const relativeUri = "/v26.0/agent-sessions/join";
    return this.#sendRequest("POST", relativeUri, "").then((data) => {
      this.updateSessionId(data.sessionId);
      return data;
    });
  }

  joinSessionAsAgent(agentId) {
    const relativeUri = "/v26.0/agent-sessions/join";
    const param = { asAgentId: agentId };
    return this.#sendRequest("POST", relativeUri, param).then((data) => {
      this.updateSessionId(data.sessionId);
      return data;
    });
  }

  /**
   * Re-attach to an existing session by sessionId (no API call).
   */
  reloadSession(sessionId) {
    this.sessionId = sessionId;
    this.storeSessionData(null, null, sessionId);
    return Promise.resolve({ sessionId });
  }

  handleError(xhr, textStatus, errorThrown) {
    this.log.warn(`[${this.#sessionPrefix}] API Error: ${textStatus} ${errorThrown}`);
    throw new IncontactApiError(xhr.status, xhr.statusText, errorThrown, xhr.responseText);
  }

  endSession(forceLogout = true, endContacts = true, ignorePersonalQueue = true) {
    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}?forceLogoff=${forceLogout}&endContacts=${endContacts}&ignorePersonalQueue=${ignorePersonalQueue}`;
    return this.#sendRequest("DELETE", relativeUri, "").then(() => {
      const logoutUrl = `${this.#currentAuth.logoutUrl}?id_token_hint=${encodeURIComponent(this.#currentAuth.idToken)}`;
      this.clearSessionData();
      this.#currentAuth.remove();
      return logoutUrl;
    });
  }

  clearSessionData() {
    sessionStorage.removeItem(this.#getStorageKey("sessionId"));
    sessionStorage.removeItem(this.#getStorageKey("reloadSessionId"));
  }

  nextEvents() {
    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/get-next-event?timeout=60`;
    return this.#sendRequest("GET", relativeUri, null, 65 * 1000).then((data, statusText) => {
      return this.handleNextEvents(data, statusText);
    });
  }

  handleNextEvents(data, statusText) {
    const events = [];

    if (data.sessionId) {
      this.updateSessionId(data.sessionId);
    }

    if (statusText === "Session Ended") {
      this.clearSessionData();
      events.push(new AgentSessionEndEvent());
    } else if (data && data.events) {
      data.events.forEach((ev) => {
        events.push(this.createEvent(ev));
      });
    }
    return events;
  }

  updateSessionId(sessionId) {
    this.sessionId = sessionId;
    this.storeSessionData();
  }

  createEvent(ev) {
    this.log.info(`[${this.#sessionPrefix}] Received Event: %s`, ev.Type || ev.type);
    switch (ev.Type) {
      case "AgentLeg":
        return new AgentLegEvent(ev);
      case "AgentState":
        return new AgentStateEvent(ev);
      case "AgentSessionStart":
        return new AgentSessionStartEvent(ev);
      case "AgentSessionEnd":
        return new AgentSessionEndEvent(ev);
      case "CallContactEvent":
        return new CallContactEvent(ev);
      case "AgentError":
        return new AgentErrorEvent(ev);
      case "WorkItemContactEvent":
        return new WorkItemEvent(ev);
      case "UpdateSkills":
        return new UpdateSkillsEvent(ev);
      default:
        return null;
    }
  }

  attachData(contactId, indicatorName, data) {
    if (!contactId || !indicatorName || !data) {
      return Promise.reject(
        new IncontactApiError("400", "Must supply contactId, indicatorName, and data"),
      );
    }

    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/${encodeURIComponent(contactId)}/custom-data`;
    const dataBody = this.formatDataBody(indicatorName, data);
    return this.#sendRequest("POST", relativeUri, dataBody);
  }

  formatDataBody(indicatorName, data) {
    if (typeof data === "object") {
      const params = new URLSearchParams();
      params.append("indicatorName", indicatorName);

      const entries = Object.entries(data)
        .map(([key, value]) => `${key}=${value}`)
        .join("|");
      params.append("data", entries);

      return params.toString();
    } else {
      const params = new URLSearchParams();
      params.append("indicatorName", indicatorName);
      params.append("data", data);
      return params.toString();
    }
  }

  setAgentState(state, outstateReason) {
    if (!state) {
      return Promise.reject(
        new IncontactApiError("400", "Must supply the state to set the agent on"),
      );
    }

    let relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/state?state=${encodeURIComponent(state)}`;
    if (state === "Unavailable" && outstateReason) {
      relativeUri += `&reason=${encodeURIComponent(outstateReason)}`;
    }
    return this.#sendRequest("POST", relativeUri, "");
  }

  getUnavailableCodes(teamId) {
    return this.#sendRequest(
      "GET",
      `/v26.0/admin/teams/${encodeURIComponent(teamId)}/unavailable-codes`,
      "",
    );
  }

  getAgentInfo(agentId) {
    return this.#sendRequest("GET", `/v26.0/agents/${agentId}`, "").then(
      (data) => new AgentInfo(data.agents[0]),
    );
  }

  getAgentInfoSync(agentId) {
    return this.#sendRequest("GET", `/v26.0/agents/${agentId}`, "");
  }

  getAgentState(agentId) {
    return this.#sendRequest("GET", `/v26.0/agents/${agentId}/states`, "");
  }

  getAgentPermissions(agentId) {
    return this.#sendRequest("GET", `/v26.0/permissions/${agentId}`, "");
  }

  getAgentList(agentId) {
    return this.getAgentInfo(agentId);
  }

  getAgentSkills(agentId) {
    return this.#sendRequest(
      "GET",
      `/v26.0/agents/${agentId}/skills`,
      "isSkillActive=true&isAssignedSkillActive=true",
    ).then((data) => data.agentSkillAssignments.map((skill) => new SkillInfo(skill)));
  }

  getAgentOutboundSkills(agentId) {
    return this.#sendRequest(
      "GET",
      `/v26.0/agents/${agentId}/skills`,
      "isAssignedSkillActive=true",
    ).then((data) => data.agentSkillAssignments.filter((skill) => new SkillInfo(skill).isOutbound));
  }

  getAgentSettings(agentId) {
    return this.#sendRequest("GET", `/v26.0/agents/${agentId}/agent-settings`, "");
  }

  getAllAgents(param) {
    return this.#sendRequest("GET", "/v26.0/agents", param);
  }

  getAllActiveContacts(param) {
    return this.#sendRequest("GET", "/v26.0/contacts/active", param);
  }

  getAllAgentStates(param) {
    return this.#sendRequest("GET", "/v26.0/agents/states", param);
  }

  endContact(contactId) {
    return this.#sendRequest(
      "POST",
      `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/${contactId}/end`,
      "",
    );
  }

  resumeContact(contactId) {
    return this.#sendRequest(
      "POST",
      `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/${contactId}/resume`,
      "",
    );
  }

  holdContact(contactId) {
    return this.#sendRequest(
      "POST",
      `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/${contactId}/hold`,
      "",
    );
  }

  dialSkill(parentContactId, skillId) {
    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/dial-skill`;
    const params = new URLSearchParams();
    params.append("parentContactId", parentContactId);
    params.append("skillId", skillId);
    return this.#sendRequest("POST", relativeUri, params.toString());
  }

  dialAgent(parentContactId, agentId, skillId) {
    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/dial-skill`;
    const params = new URLSearchParams();
    params.append("parentContactId", parentContactId);
    params.append("targetAgentId", agentId);
    params.append("skillId", skillId);
    return this.#sendRequest("POST", relativeUri, params.toString());
  }

  dialPhoneNumber(parentContactId, number, skillId, callerId) {
    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/dial-phone`;
    const params = new URLSearchParams();
    params.append("parentContactId", parentContactId);
    params.append("phoneNumber", number);
    params.append("skillId", skillId);
    params.append("callerId", callerId);
    return this.#sendRequest("POST", relativeUri, params.toString());
  }

  transferCalls() {
    return this.#sendRequest(
      "POST",
      `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/transfer-calls`,
      "",
    );
  }

  conferenceCalls() {
    return this.#sendRequest(
      "POST",
      `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/conference-calls`,
      "",
    );
  }

  endAgentLeg() {
    return this.#sendRequest(
      "POST",
      `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/agent-phone/end`,
      "",
    );
  }

  interpreterData(since, fields) {
    let relativeUri = "/v26.0/agents/states";
    const params = [];
    if (fields) {
      params.push(`fields=${encodeURIComponent(fields)}`);
    }
    if (since) {
      params.push(`updatedSince=${encodeURIComponent(since)}`);
    }
    if (params.length) {
      relativeUri += "?" + params.join("&");
    }
    return this.#sendRequest("GET", relativeUri, "");
  }

  acceptContact(contactId) {
    return this.#sendRequest(
      "POST",
      `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/${contactId}/accept`,
      "",
    );
  }

  rejectContact(contactId) {
    return this.#sendRequest(
      "POST",
      `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/${contactId}/reject`,
      "",
    );
  }

  queueStats() {
    return this.#sendRequest("GET", "/v26.0/skills/activity", "");
  }

  signal(contactId, param) {
    return this.#sendRequest("POST", `/v26.0/interactions/${contactId}/signal`, param);
  }

  sendDtmf(digits) {
    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/send-dtmf`;
    const params = new URLSearchParams();
    params.append("dtmfSequence", digits);
    params.append("toneDurationMS", "200");
    return this.#sendRequest("POST", relativeUri, params.toString());
  }

  getSkills() {
    return this.#sendRequest("GET", "/v26.0/skills", "isActive=true&fields=skillId");
  }

  getSkillsAssignments(param) {
    return this.#sendRequest("GET", "/v26.0/agents/skills", param);
  }

  getSkillInfo(skillId) {
    return this.#sendRequest("GET", `/v27.0/skills/${encodeURIComponent(skillId)}`, "");
  }

  transferWorkItemToSkill(contactId, skill) {
    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/${contactId}/transfer-work-item-to-skill`;
    const param = { targetSkillName: skill };
    return this.#sendRequest("POST", relativeUri, param);
  }

  transferWorkItemToAgent(contactId, agentLogin) {
    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/interactions/${contactId}/transfer-work-item-to-agent`;
    const param = { targetAgentName: agentLogin };
    return this.#sendRequest("POST", relativeUri, param);
  }

  monitorAgent(targetAgentId) {
    const relativeUri = `/v26.0/agent-sessions/${encodeURIComponent(this.#sessionId)}/monitor`;
    const param = { targetAgentId: targetAgentId };
    return this.#sendRequest("POST", relativeUri, param);
  }

  async helperDetermineAgentsWithSkills() {
    const agentId = sessionStorage.getItem(this.#getStorageKey("agentId"));

    const agents = await this.getAllAgentStates(agentId).then((data) => {
      return data.agentStates.reduce((filteredAgents, agentState) => {
        if (agentState.agentStateName && agentState.agentStateName.toUpperCase() === "LOGGEDOUT") {
          return filteredAgents;
        }
        if (Number(agentState.agentId) === Number(this.agentId)) {
          return filteredAgents;
        }
        filteredAgents[agentState.agentId] = agentState;
        return filteredAgents;
      }, {});
    });

    const skillIds = await this.getSkills().then((data) => data.skills);

    const agentsWithSkills = await this.getSkillsAssignments(skillIds).then((data) => {
      return data.agentSkillAssignments.reduce((filteredAgents, skillAssignment) => {
        if (skillAssignment.isOutbound || !skillAssignment.isActive) {
          return filteredAgents;
        }
        if (agents[skillAssignment.agentId]) {
          filteredAgents[skillAssignment.agentId] = agents[skillAssignment.agentId];
        }
        return filteredAgents;
      }, {});
    });

    return agentsWithSkills;
  }
}
